import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AcornJar } from "@/components/AcornJar";
import { SorryStepCard } from "@/components/SorryStepCard";
import { SorryResult } from "@/components/SorryResult";
import { MilestoneCelebration } from "@/components/MilestoneCelebration";
import { PrintableCards } from "@/components/PrintableCards";
import { CARD_SETS } from "@/lib/cards";
import { useFamilyProfile } from "@/hooks/useFamilyProfile";
import { useAcornJar } from "@/hooks/useAcornJar";
import { SORRY_STEPS, problemSentence, repairSentence } from "@/lib/sorry";
import { coachApology, saveApology } from "@/lib/api";
import { Nice } from "@/components/Nice";
import { ParentGoldenShortcut } from "@/components/ParentGoldenShortcut";

const EMPTY = { problem: { feel: "", did: "" }, sorry: "", repair: "" };

export default function SorrySteps() {
  const age = "kid";
  const { profile } = useFamilyProfile();
  const { jar, refresh, milestone, dismissMilestone } = useAcornJar(profile);
  const [to, setTo] = useState("");
  const [step, setStep] = useState(-1);
  const [parts, setParts] = useState(EMPTY);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const body = () => ({
    problem: problemSentence(parts.problem.feel, parts.problem.did), sorry: parts.sorry.trim(), repair: repairSentence(parts.repair),
  });

  const next = async () => {
    if (step < 2) return setStep(step + 1);
    setBusy(true);
    try {
      setResult(await coachApology({ age, to, ...body() }));
      setStep(3);
    } catch { toast.error("Couldn't put it together. Try again."); }
    setBusy(false);
  };

  const said = async () => {
    setBusy(true);
    try {
      await saveApology({ age, to, ...body(), apology: result.apology,
        profile_id: profile?.id, profile_name: profile?.name });
      setDone(true);
      refresh();
    } catch {
      toast.error("We couldn't save your apology. Please try again.");
    }
    setBusy(false);
  };

  const restart = () => { setParts(EMPTY); setTo(""); setResult(null); setDone(false); setStep(-1); };

  return (
    <div className={`pp-app mode-${age}`} data-testid="sorry-page">
      <header className="pp-header">
        <Brand />
        <div className="flex items-center gap-2">
          <AcornJar jar={jar} />
          <Link to="/" className="pp-ghost" data-testid="sorry-back-link"><ArrowLeft className="h-4 w-4" /> Back to <Nice /></Link>
        </div>
      </header>
      <main className="pp-main">
        <p className="pp-label">The 3 Steps of an Apology{profile ? ` · ${profile.name}` : ""}</p>
        {step === -1 && (
          <section className="pp-card" data-testid="sorry-intro-card">
            <h1 className="pp-h1">The 3 Steps of an Apology</h1>
            <p className="pp-sub">Saying sorry takes courage. These three steps make it real, and you'll earn a golden acorn at the end.</p>
            <ol className="pp-steps" data-testid="sorry-steps-overview">
              <li><span>1</span><p><b>State the problem.</b> “You probably feel ____, because I ____.” This teaches empathy and ownership.</p></li>
              <li><span>2</span><p><b>Make the apology.</b> “I'm sorry.” On its own it can feel overused, but after step 1 it's genuine.</p></li>
              <li><span>3</span><p><b>Do the repair work.</b> This is the only way the apology is complete. Not sure how? Ask, “How can I make it better?”</p></li>
            </ol>
            <input className="pp-input pp-sorry-to" placeholder="Who are you saying sorry to? (optional)" value={to}
              maxLength={40} onChange={(e) => setTo(e.target.value)} data-testid="sorry-to-input" />
            <div className="pp-actions">
              <button className="pp-primary" onClick={() => setStep(0)} data-testid="sorry-start-button">Let's start</button>
            </div>
          </section>
        )}
        {step === -1 && (
          <p className="pp-feedback small mt-6" data-testid="sorry-parent-golden-tip">
            <b>Parents:</b> when you hear your child give all 3 steps of an apology in real life, you can give them a golden acorn in the{" "}
            <Link to="/family" className="underline" data-testid="sorry-family-link">Family area</Link>.
          </p>
        )}
        {step === -1 && <PrintableCards names={CARD_SETS.apology} />}
        {step >= 0 && step <= 2 && (
          <SorryStepCard key={step} index={step} step={SORRY_STEPS[step]} age={age} value={parts[SORRY_STEPS[step].key]}
            onChange={(v) => setParts({ ...parts, [SORRY_STEPS[step].key]: v })}
            onNext={next} onBack={() => setStep(step - 1)} />
        )}
        {busy && step === 2 && <p className="pp-sub mt-4 flex gap-2" data-testid="sorry-loading"><Loader2 className="h-5 w-5 animate-spin" /> Putting it together…</p>}
        {step === 3 && result && <SorryResult age={age} result={result} done={done} saving={busy} onSaid={said} onRestart={restart} />}
        {step === 3 && done && <ParentGoldenShortcut key={result?.apology} profile={profile} reason="Gave all 3 steps of an apology" onGiven={refresh} />}
      </main>
      <Footer />
      <MilestoneCelebration milestone={milestone} name={profile?.name} onClose={dismissMilestone} />
    </div>
  );
}
